import { useEffect, useState } from "react";
import { FileText, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const FIELDS = [
  ["name", "Emlak Ofisinin Tam Adı", 2],
  ["company_name", "Şirketin Tam Adı (Ünvan)", 2],
  ["chamber_reg_no", "Oda Sicil Numarası", 1],
  ["trade_reg_no", "Ticaret Sicil Numarası", 1],
  ["activity_no", "Faaliyet Numarası", 1],
  ["license_no", "Yetki Belgesi Numarası", 1],
  ["phone", "Sabit Telefon", 1, "0212 555 01 00"],
  ["mobile", "Cep Telefonu", 1, "0532 555 01 00"],
  ["city", "Şehir", 1],
];
const EMPTY = Object.fromEntries([...FIELDS.map(([k]) => [k, ""]), ["address", ""], ["office_code", ""], ["package_code", "STANDARD"]]);
const MAX_MB = 10;

export const OfficeFormDialog = ({ office, open, onClose, onSaved }) => {
  const isEdit = !!office;
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(isEdit ? { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, office[k] ?? EMPTY[k]])) } : EMPTY);
      setFile(null);
    }
  }, [open, office]);

  const pickFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) return toast.error("Yalnızca PDF dosyası yükleyebilirsiniz");
    if (f.size > MAX_MB * 1024 * 1024) return toast.error(`Dosya en fazla ${MAX_MB} MB olabilir`);
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { office_code, package_code, ...info } = form;
      const saved = isEdit
        ? (await api.put(`/offices/${office.id}`, info)).data
        : (await api.post("/offices", form)).data;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        try {
          await api.post(`/offices/${saved.id}/license`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (upErr) {
          toast.error(`Bilgiler kaydedildi ancak PDF yüklenemedi: ${formatApiError(upErr.response?.data?.detail)}. Düzenle ile tekrar deneyin.`);
          onSaved();
          onClose();
          return;
        }
      }
      toast.success(isEdit ? "Firma bilgileri güncellendi" : "Emlak ofisi kaydedildi");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? "Firma Bilgilerini Düzenle" : "Yeni Emlak Ofisi Kaydı"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FIELDS.map(([k, label, min, ph]) => (
              <div key={k} className={k === "name" || k === "company_name" ? "sm:col-span-2" : ""}>
                <Label>{label} *</Label>
                <Input data-testid={`office-${k}`} required minLength={min} value={form[k]} onChange={set(k)} placeholder={ph} />
              </div>
            ))}
            {!isEdit && (
              <>
                <div><Label>Ofis Giriş Kodu *</Label><Input data-testid="office-code" required value={form.office_code} onChange={set("office_code")} placeholder="TR-34-XXX" /></div>
                <div>
                  <Label>Abonelik Paketi (30 gün)</Label>
                  <Select value={form.package_code} onValueChange={(v) => setForm({ ...form, package_code: v })}>
                    <SelectTrigger data-testid="office-package-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="STANDARD">Standart</SelectItem>
                      <SelectItem value="SILVER">Gümüş</SelectItem>
                      <SelectItem value="GOLD">Gold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div className="sm:col-span-2"><Label>Adres *</Label><Input data-testid="office-address" required minLength={5} value={form.address} onChange={set("address")} /></div>
          </div>

          <div>
            <Label>Yetki Belgesi (PDF, en fazla {MAX_MB} MB)</Label>
            <label className="mt-1 flex items-center gap-3 rounded-lg border-2 border-dashed border-slate-300 px-4 py-3 cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 transition-colors">
              {file ? <FileText className="w-5 h-5 text-red-600" /> : <Upload className="w-5 h-5 text-slate-400" />}
              <span className="text-sm text-slate-600 truncate">
                {file ? file.name : isEdit && office.license_document ? `Mevcut: ${office.license_document.filename} — değiştirmek için seçin` : "PDF dosyası seçin"}
              </span>
              <input data-testid="office-license-input" type="file" accept="application/pdf,.pdf" className="hidden" onChange={pickFile} />
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="outline" onClick={onClose}>İptal</Button>
            <Button data-testid="office-save-btn" type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">{saving ? "Kaydediliyor..." : "Kaydet"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
